/**
 * Helpers for describing *what* the local user is currently sharing.
 *
 * The browser reports the surface kind through `displaySurface`
 * ("monitor" | "window" | "browser"). The track label is only sometimes
 * human-readable: Chromium returns opaque handles such as `screen:0:0`,
 * `window:12345:0` or `web-contents-media-stream://…`, while Firefox returns
 * the real window title. We surface the label only when it looks like
 * something a person would actually recognise.
 */

export type ScreenShareSurface = "monitor" | "window" | "browser" | "unknown";

export interface ScreenShareSource {
  surface: ScreenShareSurface;
  /** Human-readable window/tab title, when the browser exposes one */
  label?: string;
}

/** Labels that are internal handles rather than something a user would recognise */
const OPAQUE_LABEL = /^(screen|window|monitor|audio|web-contents-media-stream)[:/]/i;

/** Generic one-word labels that carry no more information than the surface kind */
const GENERIC_LABELS = new Set(["screen", "window", "tab", "display", "entire screen"]);

const toSurface = (displaySurface?: string): ScreenShareSurface => {
  switch (displaySurface) {
    case "monitor":
    case "window":
    case "browser":
      return displaySurface;
    default:
      return "unknown";
  }
};

export const getScreenShareSource = (
  stream?: MediaStream,
): ScreenShareSource | undefined => {
  const track = stream?.getVideoTracks()[0];
  if (!track) return undefined;

  const raw = track.label?.trim();
  const isUsefulLabel =
    !!raw && !OPAQUE_LABEL.test(raw) && !GENERIC_LABELS.has(raw.toLowerCase());

  return {
    surface: toSurface(track.getSettings().displaySurface),
    label: isUsefulLabel ? raw : undefined,
  };
};

/** i18n key describing a surface kind — `call.sharing_source_*` */
export const screenShareSurfaceKey = (surface: ScreenShareSurface = "unknown") =>
  `call.sharing_source_${surface}`;
